/**
 * Public HTTP API contract (/api/v1). Request bodies are zod schemas (validated
 * server-side, reusable client-side); responses are TypeScript types.
 *
 * Conventions (deliberately close to LionsCloud so integrators find it familiar):
 *  - Success responses return the resource directly (no envelope).
 *  - Lists return { items, total, page, size } (page is zero-indexed).
 *  - Errors always return ApiErrorBody with a stable PascalCase `errorCode`.
 *  - Mutating mission requests accept an `Idempotency-Key` header.
 *  - Timestamps are ISO-8601 UTC strings.
 */
import { z } from "zod";
import { CalibrationType, ControlMode, MissionStatus, Pose2D, ResolvedOutcome, Role, Severity, WaypointType, } from "./common.js";
import { ArmJogJoint, ArmJogTwist, TeleopGroup } from "./robot-link.js";
import { RegistryEntry as RegistryEntrySchema } from "./registry.js";
export const API_ERROR_CODES = [
    "BadRequest",
    "ValidationFailed",
    "Unauthenticated",
    "InvalidLogin",
    "TokenExpired",
    "Forbidden",
    "OutOfScope",
    "NotFound",
    "Conflict",
    "IdempotencyKeyReused",
    "RobotOffline",
    "RobotNotReconciled",
    "RobotBusy",
    "OutcomeUnresolved",
    "ControlNotInstalled",
    "CapabilityMissing",
    "InterfaceNotAllowed",
    "BoundsViolation",
    "ControlLeaseRequired",
    "ControlHeldByOther",
    "EstopActive",
    "BundleInvalid",
    "ConfigOutOfDate",
    "RateLimited",
    /** arm_jog for a teleop lease whose holder has not confirmed visibility of the arm (ADR-018 D7). */
    "ArmJogNotConfirmed",
    "InternalError",
    "CommsUnavailable",
];
export const PageQuery = z.object({
    page: z.coerce.number().int().min(0).default(0),
    size: z.coerce.number().int().min(1).max(200).default(50),
});
export const SortOrder = z.enum(["asc", "desc"]);
// ───────────────────────────── auth & users ─────────────────────────────
export const LoginRequest = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(300),
});
export const ScopeType = z.enum(["global", "site", "fleet", "robot"]);
export const AssignmentInput = z
    .object({ role: Role, scopeType: ScopeType, scopeId: z.string().uuid().nullable() })
    .refine((a) => (a.scopeType === "global") === (a.scopeId === null), { message: "global scope has no scopeId; others require one" });
export const CreateUserRequest = z.object({
    email: z.string().email().max(254),
    displayName: z.string().min(1).max(120),
    password: z.string().min(10).max(300),
    assignments: z.array(AssignmentInput).default([]),
});
export const UpdateUserRequest = z.object({
    displayName: z.string().min(1).max(120).optional(),
    isActive: z.boolean().optional(),
    password: z.string().min(10).max(300).optional(),
});
export const SetAssignmentsRequest = z.object({ assignments: z.array(AssignmentInput) });
export const CreateApiTokenRequest = z.object({
    name: z.string().min(1).max(100),
    validityDays: z.number().int().min(1).max(365),
    /** Service identity role for the token: integrator or fleet_adapter. */
    role: z.enum(["integrator", "fleet_adapter"]),
    scopeType: ScopeType,
    scopeId: z.string().uuid().nullable(),
});
// ───────────────────────────── sites, fleets, robots ─────────────────────────────
export const CreateSiteRequest = z.object({ name: z.string().min(1).max(120), timezone: z.string().default("UTC") });
export const CreateFleetRequest = z.object({ siteId: z.string().uuid(), name: z.string().min(1).max(120) });
export const CreateRobotRequest = z.object({
    name: z.string().min(1).max(120),
    serial: z.string().min(1).max(64),
    siteId: z.string().uuid(),
    fleetId: z.string().uuid().nullable().default(null),
    model: z.string().default("tcr"),
});
export const UpdateRobotRequest = z.object({
    name: z.string().min(1).max(120).optional(),
    fleetId: z.string().uuid().nullable().optional(),
    siteId: z.string().uuid().optional(),
});
export const TimelineQuery = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
/**
 * Whether a robot supports a registry interface: "yes"/"no" when known, "unknown"
 * before the robot has ever connected (callers should then allow and let the
 * backend/robot decide).
 */
export function robotSupports(robot, interfaceId) {
    const { allowed, bound } = robot.interfaces;
    if (bound)
        return bound.includes(interfaceId) ? "yes" : "no";
    if (allowed.length === 0)
        return "unknown";
    return allowed.includes(interfaceId) ? "yes" : "no";
}
// ───────────────────────────── missions & commands ─────────────────────────────
export const CreateMissionRequest = z.object({
    robotId: z.string().uuid(),
    workflowId: z.string().uuid(),
    /** Optional overrides passed through to the workflow (validated against workflow param bounds). */
    params: z.record(z.unknown()).default({}),
    /** How long the platform will keep trying to start this mission. */
    ttlSec: z.number().int().min(10).max(24 * 3600).default(300),
});
/** Columns `GET /missions` can sort by. */
export const MissionSortKey = z.enum(["createdAt", "workflowName", "status", "progress", "startedAt", "finishedAt"]);
export const MissionListQuery = PageQuery.extend({
    robotId: z.string().uuid().optional(),
    status: MissionStatus.optional(),
    /** Workflow name contains (case-insensitive). */
    q: z.string().trim().max(200).optional(),
    /** Default `createdAt`, newest first. Missing values sort last; ties fall back to newest first. */
    sort: MissionSortKey.default("createdAt"),
    /** Default: `desc` for times and progress, `asc` for names and status (lifecycle order). */
    order: SortOrder.optional(),
});
/**
 * Record the outcome of a command whose outcome is `unknown`, after a technician
 * has established it on the robot. Audited; the note says how it was established.
 */
export const ResolveCommandRequest = z.object({
    outcome: ResolvedOutcome,
    note: z.string().trim().min(1).max(1000),
    confirm: z.literal(true),
});
/**
 * Import the robot's current map (the OccupancyGrid its map server publishes) as a
 * platform map in the robot's site. A new map is created unless `mapId` names an
 * existing map in that site, which then gets a new version. Requires the robot's
 * MAP_EXPORT_CAPABILITY and `map.edit` on the robot.
 */
export const StartMapImportRequest = z.object({
    /** Name for a new map ([A-Za-z0-9_.-]); default "<robot serial>-<yyyymmdd-hhmm>". */
    name: z.string().min(1).max(120).regex(/^[A-Za-z0-9_.-]+$/, "letters, digits, _ . - only").optional(),
    /** Add a version to this existing map instead of creating one. */
    mapId: z.string().uuid().optional(),
    /** Map topic on the robot. */
    topic: z.string().min(1).max(256).default("/map"),
});
export const MapImportStatus = z.enum(["pending", "running", "succeeded", "failed"]);
/**
 * 3D map layer (Phase 1: upload + view). A map version is a consistent 2D + 3D pair from
 * one mapping run in the same `map` frame (docs/design-arm-teleop-and-3d-maps.md §B.2), so
 * the point cloud is a LAYER of a version, not a separate map. One pointcloud layer per
 * version; to replace it delete it and upload again (until 3D deployment exists, Phase 2).
 *
 * Upload: POST /maps/:id/versions/:v/layers, multipart:
 *   file           .pcd (PCD v0.7 ascii | binary | binary_compressed, fields x y z [+ intensity])
 *                  or .tar.gz of a FAST-LIO run directory (localization/map_target.pcd required;
 *                  pgo/ and the rest are stored as-is for the later deployment phase)
 *   allowMismatch  "true" to accept a cloud whose XY bounds do not contain the 2D grid
 *                  (± POINTCLOUD_MATCH_TOLERANCE_M); otherwise 422 ValidationFailed
 * Limit MAX_3D_UPLOAD_BYTES (backend config, default 512 MiB). Needs `map.edit`.
 * GET  /maps/:id/versions/:v/layers                       → MapLayerDto[]          (map.view)
 * GET  /maps/:id/versions/:v/layers/:layerId/preview      → POINTCLOUD_PREVIEW bytes (map.view)
 * GET  /maps/:id/versions/:v/layers/:layerId/download     → the original upload      (map.view)
 * DELETE /maps/:id/versions/:v/layers/:layerId            → 204                      (map.edit)
 */
export const MapLayerKind = z.enum(["pointcloud"]);
export const PointCloudFormat = z.enum(["pcd", "fastlio_run"]);
export const POINTCLOUD_MATCH_TOLERANCE_M = 1.0;
/** Preview budget: the backend voxel-decimates (0.05 m, doubling) until at most this many points. */
export const POINTCLOUD_PREVIEW_MAX_POINTS = 500_000;
/** Direct invocation of an allowlisted registry interface (technician tool). */
export const InvokeInterfaceRequest = z.object({
    interfaceId: z.string(),
    args: z.record(z.unknown()).default({}),
    ttlSec: z.number().int().min(1).max(3600).default(30),
});
export const StartCalibrationRequest = z.object({ type: CalibrationType, confirm: z.literal(true) });
export const StartRecoveryRequest = z.object({
    errorCode: z.string(),
    instanceId: z.string().optional(),
    confirm: z.literal(true),
});
export const EstopRequest = z.object({ action: z.enum(["engage", "release"]), confirm: z.boolean().default(false) });
// ───────────────────────────── control ownership ─────────────────────────────
/** `operate` = mission/calibration/recovery authority; `teleop` additionally allows motion frames. */
export const RequestControlRequest = z.object({
    mode: ControlMode,
    /** Requires control.takeover; the previous owner is notified and audited. */
    takeover: z.boolean().default(false),
    durationSec: z.number().int().min(10).max(3600).default(300),
});
// ───────────────────────────── arm jog session (Phase 2a, ADR-018 D7) ─────────────────────────────
/** How the operator sees the arm. Phase 2b adds "camera". */
export const ArmJogVisibility = z.enum(["line_of_sight"]);
/**
 * POST /api/v1/robots/:id/arm/jog-session — the teleop lease holder confirms they can see the
 * robot and the space around the arm. Without it the backend refuses `arm_jog` for that lease
 * (`ArmJogNotConfirmed`). Held in memory for the lease's current authority generation: a new
 * lease, a new generation or a backend restart needs a new confirmation. Audited
 * (`arm.jog.visibility_confirmed`).
 */
export const ArmJogSessionRequest = z.object({ leaseId: z.string().uuid(), visibility: ArmJogVisibility });
export const CreateMapRequest = z.object({
    siteId: z.string().uuid(),
    name: z.string().min(1).max(120).regex(/^[A-Za-z0-9_.-]+$/, "letters, digits, _ . - only (used as robot map_name)"),
    description: z.string().max(1000).default(""),
});
export const FixtureProperties = z
    .object({
    fixtureKind: z.enum(["toilet", "urinal", "sink", "countertop", "other"]).default("toilet"),
    /** Robot-side fixture id (matches <map>.json detections) when known. */
    /** int32 on the robot (RobotState.fixture_id); master_sm refuses larger ids. */
    robotFixtureId: z.number().int().min(-2147483648).max(2147483647).nullable().default(null),
    vacuum: z.enum(["off", "low", "medium", "high"]).default("medium"),
    steamPressureKpa: z.number().min(0).max(1000).nullable().default(null),
    waterPwm: z.number().min(0).max(100).nullable().default(null),
    passes: z.number().int().min(1).max(10).default(1),
})
    .passthrough();
export const WaypointInput = z.object({
    name: z.string().min(1).max(120),
    type: WaypointType,
    pose: Pose2D,
    properties: z.record(z.unknown()).default({}),
});
/**
 * Paths are stored as centripetal Catmull-Rom control points in map-frame metres.
 * Geometry only — behaviour lives in workflows.
 */
export const SplineParams = z.object({
    type: z.literal("catmull_rom"),
    alpha: z.number().min(0).max(1).default(0.5),
    /** Sampling step in metres used when exporting to the robot. */
    sampleStep: z.number().min(0.01).max(0.5).default(0.05),
    closed: z.boolean().default(false),
});
export const PathInput = z.object({
    name: z.string().min(1).max(120),
    controlPoints: z.array(z.object({ x: z.number().finite(), y: z.number().finite() })).min(2).max(500),
    spline: SplineParams.default({ type: "catmull_rom", alpha: 0.5, sampleStep: 0.05, closed: false }),
    /** Optional waypoints this path connects (used to export a route graph). */
    startWaypointId: z.string().uuid().nullable().default(null),
    endWaypointId: z.string().uuid().nullable().default(null),
    /** Holonomic base: heading policy for sampled poses. */
    headingMode: z.enum(["tangent", "fixed"]).default("tangent"),
    fixedHeading: z.number().finite().nullable().default(null),
    speedLimit: z.number().positive().max(2).nullable().default(null),
});
/** Workflow steps reference geometry by id; the onboard mission executor interprets them. */
export const WorkflowStep = z.discriminatedUnion("type", [
    z.object({ type: z.literal("follow_path"), pathId: z.string().uuid() }),
    z.object({ type: z.literal("navigate_to"), waypointId: z.string().uuid() }),
    z.object({ type: z.literal("pass_door"), waypointId: z.string().uuid() }),
    z.object({ type: z.literal("enter_cubicle"), waypointId: z.string().uuid() }),
    z.object({
        type: z.literal("clean_fixture"),
        waypointId: z.string().uuid(),
        /** Overrides the fixture waypoint's properties for this workflow. */
        overrides: z.record(z.unknown()).default({}),
    }),
    z.object({ type: z.literal("dock"), waypointId: z.string().uuid() }),
    z.object({ type: z.literal("wait"), seconds: z.number().min(0).max(3600) }),
]);
export const WorkflowInput = z.object({
    mapId: z.string().uuid(),
    name: z.string().min(1).max(120),
    description: z.string().max(1000).default(""),
    /** Onboard behaviour/BT the executor should use (e.g. "tcr_clean_v1"). */
    behaviorRef: z.string().min(1).max(120).default("tcr_clean_v1"),
    steps: z.array(WorkflowStep).min(1).max(200),
});
// ───────────────────────────── config deployment ─────────────────────────────
export const DeploymentPreviewQuery = z.object({ mapId: z.string().uuid() });
export const CreateDeploymentRequest = z.object({
    mapId: z.string().uuid(),
    /** Explicit confirmation required for config pushes. */
    confirm: z.literal(true),
    note: z.string().max(500).default(""),
});
export const RollbackDeploymentRequest = z.object({ confirm: z.literal(true) });
// ───────────────────────────── registry & error catalog (admin) ─────────────────────────────
export const UpsertRegistryEntryRequest = RegistryEntrySchema;
export const ErrorDefinitionInput = z.object({
    /** Exact key (e.g. "hw.kinco.fault") or wildcard prefix ending in ".*" (e.g. "hw.motor.*"). */
    code: z.string().min(1).max(128).regex(/^[A-Za-z0-9_.-]+(\.\*)?$/),
    title: z.string().min(1).max(200),
    description: z.string().max(4000).default(""),
    severity: Severity,
    subsystem: z.string().max(64).default("general"),
    operatorSteps: z.array(z.string().max(500)).max(20).default([]),
    technicianSteps: z.array(z.string().max(500)).max(20).default([]),
    /** Approved recovery: a registry service/action to invoke. */
    recovery: z
        .object({
        interfaceId: z.string(),
        args: z.record(z.unknown()).default({}),
        label: z.string().max(80).default("Attempt recovery"),
        /** Whether operators (not only technicians) may trigger it. */
        operatorAllowed: z.boolean().default(false),
        /** Pass the error instance id into args at this key (e.g. "error_id"). */
        instanceArg: z.string().nullable().default(null),
    })
        .nullable()
        .default(null),
});
/** Columns `GET /audit` can sort by. */
export const AuditSortKey = z.enum(["at", "actorName", "action", "result"]);
export const AuditQuery = PageQuery.extend({
    robotId: z.string().uuid().optional(),
    actorId: z.string().uuid().optional(),
    action: z.string().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    /** Default `at`, newest first; ties fall back to recording order. */
    sort: AuditSortKey.default("at"),
    /** Default: `desc` for `at`, `asc` otherwise. */
    order: SortOrder.optional(),
});
// ───────────────────────────── realtime (browser WebSocket) ─────────────────────────────
/**
 * Browser realtime socket: GET /api/v1/realtime?ticket=<one-time ticket>
 * (obtain via POST /api/v1/realtime/tickets). Topics:
 *   fleet                 — connectivity/state summaries of robots in scope
 *   robot:<id>            — state, telemetry, errors, control, commands for one robot
 *   mission:<id>          — lifecycle updates for one mission
 * Every subscribe is scope-checked exactly like the equivalent HTTP GET.
 *
 * Event payloads (`data`) by kind:
 *   robot.updated       RobotDto (possibly partial)
 *   robot.telemetry     { robotId, samples: TelemetryValueDto[] } — same shape as GET /robots/:id/telemetry
 *   robot.errors        { robotId, errors: ActiveErrorDto[] }
 *   control.changed     { robotId, control: ControlOwnerDto | null }
 *   command.updated     CommandDto
 *   mission.updated     MissionDto
 *   calibration.updated CalibrationRunDto
 *   deployment.updated  ConfigDeploymentDto
 *   map_import.updated  MapImportDto
 *   teleop.status       TeleopStatusBody & { robotId }  (group absent = base)
 *
 * `arm_jog` (Phase 2a): exactly one of `twist` / `joint`; the backend adds generation and holdMs.
 * `teleop_stop.group`: stop only that stream; absent = base and arm.
 */
export const RealtimeClientMessage = z
    .discriminatedUnion("type", [
    z.object({ type: z.literal("subscribe"), topics: z.array(z.string()).min(1).max(100) }),
    z.object({ type: z.literal("unsubscribe"), topics: z.array(z.string()).min(1).max(100) }),
    z.object({
        type: z.literal("teleop"),
        robotId: z.string().uuid(),
        leaseId: z.string().uuid(),
        seq: z.number().int().nonnegative(),
        vx: z.number().finite(),
        vy: z.number().finite(),
        wz: z.number().finite(),
    }),
    z.object({
        type: z.literal("arm_jog"),
        robotId: z.string().uuid(),
        leaseId: z.string().uuid(),
        seq: z.number().int().nonnegative(),
        frame: z.enum(["tool", "base"]),
        twist: ArmJogTwist.optional(),
        joint: ArmJogJoint.optional(),
    }),
    z.object({ type: z.literal("teleop_stop"), robotId: z.string().uuid(), leaseId: z.string().uuid(), group: TeleopGroup.optional() }),
    z.object({ type: z.literal("ping"), nonce: z.string() }),
])
    .superRefine((m, ctx) => {
    if (m.type === "arm_jog" && (m.twist === undefined) === (m.joint === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["twist"], message: "exactly one of twist or joint" });
    }
});
// Robot-facing HTTP (token exchange, bundle download, artifact upload)
export const RobotTokenRequest = z.object({ robotId: z.string().uuid(), secret: z.string().min(16) });
//# sourceMappingURL=api.js.map